var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useMediaQuery, useTheme } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { enUS, nlNL, frFR, deDE } from "@mui/x-date-pickers/locales";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import { isSameDay, isBefore, getWeeksInMonth } from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useSettings } from '../../context/SettingsContext';
import { useI18N } from '../../context/I18NContext';
import useWidgetStore from '../../store/useWidgetStore';
import useDataStore from '../../store/useDataStore';
import { useFlow } from '../../context/FlowContext';
const CustomCalendarHeaderRoot = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 16px',
    alignItems: 'center',
});
function CustomCalendarHeader(props) {
    const flow = useFlow();
    const { currentMonth, onMonthChange } = props;
    const { t } = useI18N();
    const { language, selectedDate, setSelectedDate } = useWidgetStore();
    const [canSelectPast, setCanSelectPast] = useState(false);
    const selectNextMonth = () => {
        let newMonth = currentMonth.add(1, 'month');
        // @ts-ignore
        props.setDirection("right");
        onMonthChange(newMonth, 'right');
        setCanSelectPast(true);
        setSelectedDate(newMonth.toDate());
    };
    const selectPreviousMonth = () => {
        let newMonth = currentMonth.subtract(1, 'month'), now = dayjs();
        if (newMonth.isBefore(now)) {
            setCanSelectPast(false);
        }
        // @ts-ignore
        props.setDirection("left");
        onMonthChange(newMonth, 'left');
        setSelectedDate(newMonth.toDate());
    };
    useEffect(() => {
        if (selectedDate && selectedDate.length > 0) {
            let current = dayjs(selectedDate[0]), newMonth = current.subtract(1, 'month'), now = dayjs();
            setCanSelectPast(!newMonth.isBefore(now));
        }
    }, [flow.currentView]);
    // const selectNextYear = () => onMonthChange(currentMonth.add(1, 'year'), 'right')
    // const selectPreviousYear = () => onMonthChange(currentMonth.subtract(1, 'year'), 'left')
    return (_jsxs(CustomCalendarHeaderRoot, { children: [_jsx(Stack, { spacing: 1, direction: "row", children: _jsx(IconButton, { disabled: !canSelectPast, onClick: selectPreviousMonth, title: t('Vorige maand', 'select'), children: _jsx(ChevronLeft, {}) }) }), _jsx(Typography, { variant: "body2", sx: {
                    fontSize: "1.25rem",
                    fontWeight: 500
                }, children: currentMonth.locale(language).format('MMMM YYYY') }), _jsx(Stack, { spacing: 1, direction: "row", children: _jsx(IconButton, { onClick: selectNextMonth, title: t('Volgende maand', 'select'), children: _jsx(ChevronRight, {}) }) })] }));
}
export default function DatePicker(props) {
    const theme = useTheme();
    const flow = useFlow();
    const { widgetMode } = useSettings();
    const { language, selectedCapacity, selectedCapacityGroups, selectedApptype, selectedResource, setSelectedDate, selectedDate, setSelectedResource } = useWidgetStore();
    const { t } = useI18N();
    const [enablePicker, setEnablePicker] = useState(true);
    const [direction, setDirection] = useState("right");
    useEffect(() => {
        var _a, _b, _c, _d;
        setEnablePicker(selectedCapacity > 0 && selectedCapacity >= ((_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.min) !== null && _b !== void 0 ? _b : 0) && selectedCapacity <= ((_d = (_c = props.config) === null || _c === void 0 ? void 0 : _c.max) !== null && _d !== void 0 ? _d : 100));
    }, [selectedCapacity, selectedApptype, selectedCapacityGroups]);
    useEffect(() => {
        if ((selectedResource === null || selectedResource === void 0 ? void 0 : selectedResource.length) === 0) {
            let hasResourceSelect = false;
            let selectViews = flow.views.filter((view) => [
                "select", "select1", "select2"
            ].includes(view.route.route));
            selectViews.forEach((view) => {
                var _a;
                let resourceSelect = (_a = view.components) === null || _a === void 0 ? void 0 : _a.filter((component) => {
                    return component.source === "resource";
                });
                if (resourceSelect && (resourceSelect === null || resourceSelect === void 0 ? void 0 : resourceSelect.length) > 0) {
                    hasResourceSelect = true;
                }
            });
            if (!hasResourceSelect) {
                // there is no resource select in this widget, so select -1 as a default
                setSelectedResource(-1);
            }
        }
    }, [selectedResource]);
    dayjs.extend(updateLocale);
    dayjs.updateLocale('en', {
        weekStart: 1
    });
    const isLarge = useMediaQuery(theme.breakpoints.up('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm')) && widgetMode !== "standalone";
    const [localeText, setLocaleText] = useState({});
    useEffect(() => {
        if (language === 'nl')
            setLocaleText(nlNL.components.MuiLocalizationProvider.defaultProps.localeText);
        if (language === 'en')
            setLocaleText(enUS.components.MuiLocalizationProvider.defaultProps.localeText);
        if (language === 'fr')
            setLocaleText(frFR.components.MuiLocalizationProvider.defaultProps.localeText);
        if (language === 'de')
            setLocaleText(deDE.components.MuiLocalizationProvider.defaultProps.localeText);
    }, [language]);
    // we need to calculate the size because otherwise we get oval days ( we can calculate width in css, but not the height )
    const fixedSize = (window.innerWidth - 56) / 7, // width of the buttons (2x3px margin, 2x1px border, 2x 8px container padding)
    fixedHeight = ((fixedSize * 7) / 1.35) + 70; // the 1.35 is the ratio of width to height of the buttons
    const [activeDate, setActiveDate] = useState(dayjs());
    const [weeksInMonth, setWeeksInMonth] = useState(6);
    const [highlightedDays, setHighlightedDays] = useState([]);
    const { dates } = useDataStore();
    useEffect(() => {
        if (activeDate) {
            let month = activeDate.month(), hasDate = false;
            dates.forEach((bookable) => {
                // note, .month = 0 - 11
                if (dayjs(bookable.time).month() === month) {
                    hasDate = true;
                }
            });
            if (!hasDate && dates.length > 0) {
                month = dayjs(dates[0].time).month();
            }
            updateBookableDates(month);
        }
    }, [dates]);
    useEffect(() => {
        if (selectedDate) {
            setActiveDate(dayjs(selectedDate[0]));
        }
    }, [selectedDate]);
    useEffect(() => {
        if (activeDate) {
            setWeeksInMonth(getWeeksInMonth(activeDate.toDate(), { weekStartsOn: 1 }));
        }
    }, [activeDate]);
    const onMonthChange = (date) => {
        setActiveDate(date);
        updateBookableDates(date.month());
    };
    const onViewChange = (view) => {
        if (activeDate) {
            updateBookableDates(activeDate.month());
        }
    };
    const selectActiveDate = (date) => {
        setSelectedDate(date.toDate());
        setActiveDate(date);
    };
    const updateBookableDates = (month) => {
        let bookableDates = [];
        dates.forEach((bookable) => {
            // note, .month = 0 - 11
            if (dayjs(bookable.time).month() === month) {
                let t = dayjs(bookable.time);
                bookableDates.push(`${t.date()}-${t.month()}`);
            }
        });
        setHighlightedDays(bookableDates);
        let valid = bookableDates.find((date) => {
            if (selectedDate && selectedDate[0] !== undefined) {
                return date === `${dayjs(selectedDate[0]).date()}-${dayjs(selectedDate[0]).month()}`;
            }
            return false;
        });
        if (!valid) {
            if (direction === "right") {
                let firstDate = dates.find((bookable) => dayjs(bookable.time).month() === month);
                if (firstDate) {
                    setSelectedDate(dayjs(firstDate.time).toDate());
                }
            }
            else {
                // navigating back, don't try to automatically go to the "first" previous date
                // this would involve setting the active date, whichh has to take into account no dates in the past,
                // no recursion/ call stack issues etc.
            }
        }
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    function BookableDay(props) {
        const { highlightedDays = [], day, outsideCurrentMonth } = props, other = __rest(props, ["highlightedDays", "day", "outsideCurrentMonth"]);
        const isSelected = activeDate && isSameDay(day.toDate(), activeDate.toDate()), isHighlighted = enablePicker && highlightedDays.includes(`${day.date()}-${day.month()}`), 
        //Note: currently not using different colors for weekend days, but leaving
        // the option open to later re-add it through a config option.
        isWeekend = false && [0, 6].includes(day.day()), isHistory = isBefore(day.toDate(), today);
        return (_jsx(Badge, { overlap: "circular", children: _jsx(PickersDay, Object.assign({ className: isHighlighted ? '' : 'Mui-disabled' }, other, { outsideCurrentMonth: outsideCurrentMonth, day: day, sx: Object.assign({ backgroundColor: isHighlighted
                        ? theme.palette.primary.main
                        : isWeekend
                            ? theme.palette.background.default
                            : theme.palette.background.paper, color: isHighlighted
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.secondary, borderWidth: '1px', borderStyle: 'solid', borderRadius: '4px', maxHeight: '40px', fontSize: '1rem', fontWeight: isHighlighted ? 500 : 400, borderColor: isHighlighted
                        ? theme.palette.mode === 'light'
                            ? theme.palette.primary.dark
                            : theme.palette.primary.light
                        : theme.palette.text.disabled, "&:hover": {
                        backgroundColor: theme.palette.mode === 'light'
                            ? theme.palette.primary.dark
                            : theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        borderColor: theme.palette.primary.contrastText
                    }, "&.Mui-selected": {
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText,
                        borderColor: theme.palette.secondary.contrastText,
                        "&:hover": {
                            backgroundColor: theme.palette.mode === 'light'
                                ? theme.palette.secondary.light
                                : theme.palette.secondary.dark
                        }
                    }, "&.Mui-selected:focus": {
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText,
                        borderColor: theme.palette.secondary.contrastText
                    }, "&.Mui-disabled": {
                        backgroundColor: (isWeekend || isHistory)
                            ? theme.palette.background.paper
                            : theme.palette.background.default,
                        color: isWeekend
                            ? theme.palette.text.secondary
                            : theme.palette.text.disabled,
                        borderColor: (isWeekend || isHistory)
                            ? theme.palette.background.paper
                            : theme.palette.background.default
                    } }, (widgetMode === 'default' && !isDesktop && {
                    width: '12dvw !important'
                })) })) }, props.day.toString()));
    }
    return (_jsxs(Box, { sx: {
            borderWidth: '0px',
            color: theme.palette.text.primary,
            borderRadius: '4px',
            width: '100%',
        }, children: [_jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, adapterLocale: language, localeText: localeText, children: _jsx(DateCalendar, { slots: {
                        day: BookableDay,
                        calendarHeader: CustomCalendarHeader
                    }, slotProps: {
                        day: {
                            highlightedDays,
                        },
                        calendarHeader: {
                            // @ts-ignore
                            setDirection: setDirection
                        }
                    }, value: dayjs(activeDate), onChange: (newValue) => selectActiveDate(newValue), onMonthChange: onMonthChange, onViewChange: onViewChange, disablePast: true, dayOfWeekFormatter: (x, weekday) => `${weekday.format('dd')}`, views: ['day'], sx: Object.assign(Object.assign(Object.assign({ width: '100%', margin: isDesktop ? 0 : '0 auto', "& .MuiDayCalendar-slideTransition": {
                            minHeight: isLarge ? '420px' : fixedHeight
                        }, "& .MuiPickersDay-dayWithMargin": {
                            width: isLarge ? '63px' : fixedSize,
                            margin: '3px'
                        }, "& .MuiDayCalendar-weekDayLabel": {
                            width: isLarge ? '63px' : fixedSize,
                            margin: '3px',
                            fontSize: "1rem"
                        }, "& .MuiSvgIcon-root": {
                            color: theme.palette.text.primary
                        }, maxHeight: "370px" }, (weeksInMonth === 6 && {
                        height: "calc(154px + (6 * 36px))",
                    })), (weeksInMonth === 5 && {
                        height: "calc(154px + (5 * 36px))",
                    })), (weeksInMonth === 4 && {
                        height: "calc(154px + (4 * 36px))",
                    })
                    // ":has(.MuiDayCalendar-weekContainer:nth-child(4):last-child": {
                    //     height: "calc(154px + (4 * 36px))", // 36px per row, 216px total
                    // },
                    // ":has(.MuiDayCalendar-weekContainer:nth-child(5):last-child": {
                    //     height: "calc(154px + (5 * 36px))", // 36px per row, 216px total
                    // },
                    // ":has(.MuiDayCalendar-weekContainer:nth-child(6):last-child": {
                    //     height: "calc(154px + (6 * 36px))", // 36px per row, 216px total
                    // }
                    ) }) }), dates && dates.length === 0 && (_jsx(_Fragment, { children: _jsx(Typography, { variant: "subtitle1", sx: {
                        paddingX: 1,
                        marginY: 1,
                        fontSize: '1.25rem',
                        color: theme.palette.text.primary
                    }, children: t(selectedResource && (selectedResource === null || selectedResource === void 0 ? void 0 : selectedResource.length) > 0
                        ? selectedCapacity === 0
                            ? 'Kies eerst aantal personen'
                            : 'Er zijn geen dagen beschikbaar'
                        : 'Kies eerst een medewerker') }) }))] }));
}
